import { groupBy, sumBy } from 'lodash';
import { BasicButton } from 'src/design/atoms/BasicButton';
import React from 'react';

import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Text } from 'src/design/atoms/Text';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Table } from 'src/design/molecules/Table';
import { IconDeviceFloppy, IconHistory } from '@tabler/icons-react';
import {
  CostCodeAdjustmentItem,
  CreateCostCodeAdjustmentRequest,
  JobCostCodeSummaryGpDto,
} from 'src/api/FiretrolApiClient';
import { useCreateCtcAdjustment } from 'src/api/queries/ctc/useCtcAdjustmentMutations';
import { getCategoryByCode } from 'src/routes/CTC/ViewCTCJob/constants';
import { CostCodeLine } from './CostCodeLine';
import { HistoryDialog } from './HistoryDialog';
import { SummaryLine } from './SummaryLine';

interface GroupedSummary {
  [category: string]: CostCodeAdjustmentItem[];
}

interface Estimator2Props {
  summary: JobCostCodeSummaryGpDto[];
  adjustments: CostCodeAdjustmentItem[];
  initialAdjustedRev: number;
  jobId: string;
  allowed: boolean;
  onAdjustedRevChange: (value: number) => void;
  onCostAtCompletionTotalChange: (value: number) => void;
}

export const Estimator2: React.FC<Estimator2Props> = ({
  summary,
  onAdjustedRevChange,
  allowed,
  jobId,
  initialAdjustedRev,
  adjustments,
  onCostAtCompletionTotalChange,
}) => {
  const [summaryItems, setSummaryItems] = React.useState<
    CostCodeAdjustmentItem[]
  >([]);
  const [enableSave, setEnableSave] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const createAdjustment = useCreateCtcAdjustment(jobId);
  const ctcRef = React.useRef(0);
  const [rev, setRev] = React.useState(initialAdjustedRev);
  const [groupedByCategory, setGroupedByCategory] =
    React.useState<GroupedSummary>({});
  const [changes, setChanges] = React.useState<CostCodeAdjustmentItem[]>([]);

  const getMainTotals = () => {
    const totals = {
      hours: 0,
      rate: 0,
      ctc: 0,
      costAtCompletion: 0,
      overUnder: 0,
    };
    Object.keys(groupedByCategory).map((category) => {
      const items = groupedByCategory[category];
      totals.hours = totals.hours + sumBy(items, 'hours');
      totals.rate = totals.rate + sumBy(items, 'rate');
      totals.ctc = totals.ctc + sumBy(items, 'ctc');
      totals.costAtCompletion =
        totals.costAtCompletion + sumBy(items, 'costAtCompletion');
      totals.overUnder = totals.overUnder + sumBy(items, 'overUnder');
    });
    return totals;
  };

  const allTotals = React.useMemo(() => getMainTotals(), [groupedByCategory]);

  React.useEffect(() => {
    onAdjustedRevChange(rev);
  }, [rev]);

  React.useEffect(() => {
    onCostAtCompletionTotalChange(allTotals.costAtCompletion);
  }, [allTotals.costAtCompletion]);
  const toggleHistory = () => {
    setHistoryOpen((prev) => !prev);
  };

  React.useEffect(() => {
    const enhanced: CostCodeAdjustmentItem[] = summary.map((item) => {
      const existing = adjustments.find(
        (adj) => adj.costCode === item.costCode,
      );
      if (existing) {
        return {
          ...existing,
          jtdCost: item.jtdCost,
          costAtCompletion: item.jtdCost + existing.ctc,
          overUnder:
            (item.jtdCost + existing.ctc - item.revisedEstimateCost) * -1,
          revisedEstimate: item.revisedEstimateCost,
          category:
            existing.category || getCategoryByCode(item.costCode) || 'none',
        };
      }

      return {
        exclude: false,
        costCode: item.costCode.trim(),
        description: item.costCodeDescription.trim(),
        hours: item.ctcHours || 0,
        rate: item.jtdRate,
        ctc: item.ctc,
        jtdCost: item.jtdCost,
        costAtCompletion: item.jtdCost + item.ctc,
        revisedEstimate: item.revisedEstimateCost,
        overUnder: (item.jtdCost + item.ctc - item.revisedEstimateCost) * -1,
        category: getCategoryByCode(item.costCode) || 'none',
      };
    });

    const grouped = groupBy(enhanced, 'category');
    setSummaryItems(enhanced);
    setGroupedByCategory(grouped);
  }, []);

  const saveChanges = async () => {
    const filtered = adjustments.filter((a) => {
      const isChanged = !!changes.find(
        (change) => change.costCode === a.costCode,
      );
      return !isChanged;
    });
    const payload: CreateCostCodeAdjustmentRequest = {
      adjustedRev: rev,
      adjustments: [
        ...changes,
        ...filtered.map((a) => {
          return {
            ...a,
            exclude: true,
          };
        }),
      ],
    };
    await createAdjustment.mutateAsync(payload);
    setChanges((prevState) => {
      return prevState.map((c) => {
        return {
          ...c,
          exclude: true,
        };
      });
    });
    setEnableSave(false);
  };

  const onLineChange = (item: CostCodeAdjustmentItem) => {
    setGroupedByCategory((prevState) => {
      return {
        ...prevState,
        [item.category]: prevState[item.category].map((data) => {
          if (data.costCode === item.costCode) {
            return item;
          }
          return data;
        }),
      };
    });
    setChanges((prevState) => {
      const exists = prevState.find((i) => i.costCode === item.costCode);
      if (exists) {
        return prevState.map((i) => {
          if (i.costCode === item.costCode) {
            return {
              ...item,
              exclude: false,
            };
          }
          return i;
        });
      } else {
        return [
          ...prevState,
          {
            ...item,
            exclude: false,
          },
        ];
      }
    });
    setEnableSave(true);
  };

  const renderCategoryGroup = (items: CostCodeAdjustmentItem[]) => {
    return items.map((item) => {
      return (
        <CostCodeLine key={item.costCode} item={item} onChange={onLineChange} />
      );
    });
  };

  const renderGroupings = () => {
    return Object.keys(groupedByCategory).map((category) => {
      const items = groupedByCategory[category];
      const totals = {
        hours: sumBy(items, 'hours'),
        rate: sumBy(items, 'rate'),
        ctc: sumBy(items, 'ctc'),
        costAtCompletion: sumBy(items, 'costAtCompletion'),
        overUnder: sumBy(items, 'overUnder'),
      };
      return (
        <React.Fragment key={category}>
          {renderCategoryGroup(items)}
          <SummaryLine totals={totals} />
        </React.Fragment>
      );
    });
  };

  return (
    <Paper>
      <Group justify="space-between" px="sm" bg="#9ed6fc" h={50}>
        <Text size="md" c="black">
          Current Month Adjustments
        </Text>
        <Group gap="md">
          {enableSave && allowed && (
            <BasicButton
              size="sm"
              onClick={saveChanges}
              loading={createAdjustment.isPending}
              disabled={createAdjustment.isPending}
              leftSection={<IconDeviceFloppy />}
            >
              Save Adjustments
            </BasicButton>
          )}
          <BasicButton size="sm" variant="outline" onClick={toggleHistory}>
            <IconHistory />
          </BasicButton>
        </Group>
      </Group>
      <Group
        bg="#e9e9e9"
        h={40}
        px="sm"
        style={{ borderBottom: '1px solid #000' }}
        justify="space-between"
      >
        <Text size="md" c="black">
          Revenue Adjusted
        </Text>
        <NumberInput
          value={rev}
          onChange={(value) => setRev(Number(value))}
          decimalScale={2}
          prefix="$"
          placeholder="Adj Revenue"
          size="xs"
        />
      </Group>
      <Table.ScrollContainer
        minWidth={600}
        style={{ maxHeight: 1000, overflow: 'auto' }}
      >
        <Table className="header-table">
          <Table.Thead>
            <Table.Tr>
              {columns.map((column) => (
                <Table.Th
                  key={column.id}
                  align={column?.align ? column.align : 'center'}
                  style={{
                    top: 57,
                    backgroundColor: '#e9e9e9',
                    paddingLeft: column.paddingLeft || 0,
                  }}
                >
                  {column.label}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody className="ctc-table-footer">
            {renderGroupings()}
            <SummaryLine totals={allTotals} redBg />
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <HistoryDialog jobId={jobId} open={historyOpen} onClose={toggleHistory} />
    </Paper>
  );
};

interface Column {
  id: string;
  label: string;
  align?: 'right' | 'left' | 'center';
  paddingLeft?: number;
  format?: (value: number) => string;
}

const columns: Column[] = [
  { id: 'description', label: 'Description', paddingLeft: 5 },
  {
    id: 'ctcHours',
    label: 'CTC_Hours',
    format: (value: number) => value.toLocaleString('en-US'),
  },
  {
    id: 'jtdRate',
    label: 'JTD_Rate',
    format: (value: number) => value.toLocaleString('en-US'),
  },
  {
    id: 'ctc',
    label: 'CTC',
    paddingLeft: 50,
    format: (value: number) => value.toLocaleString('en-US'),
  },
  {
    id: 'costAtCompletion',
    label: 'C@C',
    format: (value: number) => value.toLocaleString('en-US'),
  },
  {
    id: 'currentDiff',
    label: '+ / -',
    format: (value: number) => value.toLocaleString('en-US'),
  },
];
